import { useCallback } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CALL_ANALYTICS_HERO_STATS } from 'src/graphql/callAnaltyics.graphql'
import { HeroStatCard } from 'src/pages/AnalyticsPage/components/HeroStatCard'
import {
  buildDelta,
  formatCount,
  formatDuration,
  percentOf,
} from 'src/pages/AnalyticsPage/components/heroStatHelpers'

import type { CallLogFiltersInput } from 'types/graphql'

export const CallAnalyticsHeroStrip = ({
  filters,
}: {
  filters: CallLogFiltersInput
}) => {
  const onError = useCallback(() => {
    toast('Failed to load call analytics summary', 'error')
  }, [])

  const { data, loading } = useQuery(GET_CALL_ANALYTICS_HERO_STATS, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  const stats = data?.callAnalyticsHeroStats
  const current = stats?.current
  const previous = stats?.previous

  const totalDelta = buildDelta({
    current: current?.totalCalls,
    previous: previous?.totalCalls,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const outboundDelta = buildDelta({
    current: current?.outboundCalls,
    previous: previous?.outboundCalls,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const inboundDelta = buildDelta({
    current: current?.inboundCalls,
    previous: previous?.inboundCalls,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const avgDurationDelta = buildDelta({
    current: current?.avgDurationSeconds,
    previous: previous?.avgDurationSeconds,
    format: 'duration',
    unit: 'duration',
    currentDateRange: filters.dateRange,
  })
  const aiDelta = buildDelta({
    current: current?.aiCalls,
    previous: previous?.aiCalls,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: filters.dateRange,
  })
  const timeSavedDelta = buildDelta({
    current: current?.timeSavedSeconds,
    previous: previous?.timeSavedSeconds,
    format: 'duration',
    unit: 'duration',
    currentDateRange: filters.dateRange,
  })

  const outboundShare = percentOf(current?.outboundCalls, current?.totalCalls)
  const inboundShare = percentOf(current?.inboundCalls, current?.totalCalls)
  const aiShare = percentOf(current?.aiCalls, current?.totalCalls)

  return (
    <div
      className="mb-4 grid grid-cols-6 gap-3"
      data-testid="call-analytics-hero-strip"
    >
      <HeroStatCard
        label="Total Calls"
        value={formatCount(current?.totalCalls)}
        loading={loading}
        loadingShowDelta
        delta={totalDelta}
        info={
          'All calls in the period (inbound + outbound, human + voice agent).' +
          (current
            ? `\n\n${formatCount(current.inboundCalls)} inbound · ${formatCount(
                current.outboundCalls
              )} outbound`
            : '')
        }
      />
      <HeroStatCard
        label="Outbound Calls"
        value={formatCount(current?.outboundCalls)}
        loading={loading}
        loadingShowDelta
        delta={outboundDelta}
        info={
          'Calls we placed (employees + voice agent).' +
          (outboundShare ? `\n\n${outboundShare} of total calls` : '')
        }
      />
      <HeroStatCard
        label="Inbound Calls"
        value={formatCount(current?.inboundCalls)}
        loading={loading}
        loadingShowDelta
        delta={inboundDelta}
        info={
          'Calls candidates initiated to us.' +
          (inboundShare ? `\n\n${inboundShare} of total calls` : '')
        }
      />
      <HeroStatCard
        label="Avg Call Duration"
        value={formatDuration(current?.avgDurationSeconds)}
        loading={loading}
        loadingShowDelta
        delta={avgDurationDelta}
        info="Average length of CONNECTED calls in the period. Excludes no-answer, busy, and failed calls."
      />
      <HeroStatCard
        label="AI Calls"
        value={formatCount(current?.aiCalls)}
        loading={loading}
        loadingShowDelta
        delta={aiDelta}
        info={
          'Calls placed by the voice agent.' +
          (aiShare ? `\n\n${aiShare} of total calls` : '')
        }
      />
      <HeroStatCard
        label="Time Saved"
        value={formatDuration(current?.timeSavedSeconds)}
        loading={loading}
        loadingShowDelta
        delta={timeSavedDelta}
        featured
        info="Total duration of every voice agent call attempt in the period — employee time the agent handled instead. Includes ring time on no-answer / busy / failed calls. Mirrors the AI row's Total Time on the Call Activity table."
      />
    </div>
  )
}
